import { motion } from 'framer-motion';
import { GraduationCap, Tv, Music, BookOpen, Award, Youtube, ExternalLink, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const timeline = [
  { year: 'Education', title: 'BA in Social Science', description: 'Foundation in sociology and cultural studies' },
  { year: 'Oregon State', title: 'Super Truffle Development', description: 'Capstone project creating a revolutionary health supplement' },
  { year: 'YouTube', title: 'MGN1.6 Channel Launch', description: '237+ videos across multiple content brands' },
  { year: 'Today', title: 'Critically Acclaimed Author', description: 'Continued creative work spanning music, satire, and storytelling' },
];

const brands = [
  { 
    name: "The Who Cares News™", 
    icon: Tv, 
    description: "Satirical newscast hosted by Grad Fawkes, the Pixar-style anthropomorphic fox in a maroon graduation gown. Reporting on what matters... or doesn't.",
    color: "text-secondary"
  },
  { 
    name: "GradTrax", 
    icon: Music, 
    description: "60+ original songs organically created, written, and produced with AI tools. From recipe songs to social commentary.",
    color: "text-accent"
  },
  { 
    name: "WTFairytale", 
    icon: BookOpen, 
    description: "Fairytale adaptations that flip the script. Classic stories reimagined through a modern, satirical lens.",
    color: "text-primary"
  },
  { 
    name: "Social Science Courses", 
    icon: GraduationCap, 
    description: "Sociology 180 where songwriting and storytelling flip the script. Educational content with a creative twist.",
    color: "text-secondary"
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-background to-background" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-[150px]" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="badge-accent mb-6 inline-block">Critically Acclaimed Author</span>
              
              <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-none mb-6" style={{ fontFamily: 'Playfair Display' }}>
                About<br />
                <span className="text-secondary">Grad P Fawkes™</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
                Creator, author, satirist, and musician. With a BA in Social Science, I blend academic insight 
                with creative storytelling to produce content that entertains, educates, and occasionally offends.
              </p>

              <a 
                href="https://youtube.com/@mgn1.6?si=BapxMq9KCFhLK9rk" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="btn-primary h-14 px-8 text-lg" data-testid="youtube-link">
                  <Youtube className="w-5 h-5 mr-2" />
                  Visit YouTube Channel
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              
              <a 
                href="https://www.linkedin.com/in/gradgradfawkes-mgn1point6" 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-4 inline-block"
              >
                <Button variant="outline" className="h-14 px-8 text-lg gap-2" data-testid="linkedin-link">
                  <Linkedin className="w-5 h-5" />
                  LinkedIn
                </Button>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl overflow-hidden border border-border/50 glow-maroon">
                <img
                  src="https://images.unsplash.com/photo-1655532391070-ef6c6e922e39?w=600"
                  alt="Grad P Fawkes Character"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Stats floating cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-6 -left-6 bg-card border border-border rounded-2xl p-4 shadow-xl"
              >
                <p className="text-3xl font-black text-secondary">237+</p>
                <p className="text-sm text-muted-foreground">Videos Created</p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="absolute -top-6 -right-6 bg-card border border-border rounded-2xl p-4 shadow-xl"
              >
                <p className="text-3xl font-black text-accent">60</p>
                <p className="text-sm text-muted-foreground">Original Songs</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-header mb-4">The Journey</h2>
            <p className="section-subheader max-w-2xl mx-auto">
              From academic foundations to creative empire
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 mb-8 last:mb-0"
              >
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-full bg-border mt-2" />
                  )}
                </div>
                <div className="pb-8">
                  <span className="text-sm font-bold text-secondary uppercase tracking-wider">
                    {item.year}
                  </span>
                  <h3 className="text-xl font-bold mt-1 mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-24 md:py-32">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-header mb-4">The Brands</h2>
            <p className="section-subheader max-w-2xl mx-auto">
              Four distinct creative universes under one satirical umbrella
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {brands.map((brand, index) => (
              <motion.div
                key={brand.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-card border-border/50 h-full card-hover">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-2xl bg-muted flex items-center justify-center flex-shrink-0`}>
                        <brand.icon className={`w-7 h-7 ${brand.color}`} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                          {brand.name}
                        </h3>
                        <p className="text-muted-foreground">{brand.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Super Truffle Story */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge-accent mb-4 inline-block">Oregon State University</span>
              <h2 className="section-header mb-6">The Super Truffle Story</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Super Truffle began as a capstone project at Oregon State University. The goal was simple: 
                create a delicious, comprehensive health supplement that people would actually enjoy taking every day.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Through extensive research, I developed a formula infused with 102 trace minerals and 9 essential 
                amino acids. The result is a supplement that supports overall health while tasting great.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Today, Super Truffle continues to help people on their wellness journey—one delicious serving at a time.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-video rounded-3xl overflow-hidden border border-accent/30" style={{ boxShadow: '0 0 40px rgba(244, 196, 48, 0.15)' }}>
                <img
                  src="https://images.unsplash.com/photo-1556740758-90de374c12ad?w=800"
                  alt="Super Truffle Development"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact/Connect */}
      <section className="py-24 md:py-32">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-header mb-6">Connect With Me</h2>
            <p className="section-subheader max-w-2xl mx-auto mb-8">
              Follow the journey on YouTube and social media. New content drops regularly.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="https://youtube.com/@mgn1.6?si=BapxMq9KCFhLK9rk" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="btn-primary h-14 px-8" data-testid="connect-youtube">
                  <Youtube className="w-5 h-5 mr-2" />
                  @mgn1.6
                </Button>
              </a>
              <a 
                href="https://open.spotify.com/artist/2m8OD1Y5PSQmd5mBPQvR3r" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="btn-outline h-14 px-8" data-testid="connect-spotify">
                  <Music className="w-5 h-5 mr-2" />
                  Spotify
                </Button>
              </a>
              <a 
                href="https://www.amazon.com/vdp/08c772b5c6a44f21a04ff1d503385e9b?product=B0FWYHS5KG" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="btn-outline h-14 px-8" data-testid="connect-amazon">
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Amazon
                </Button>
              </a>
            </div>

            <Separator className="my-12 max-w-md mx-auto" />

            <p className="text-muted-foreground text-sm">
              "FUNNY - REAL - sociology 180 where songwriting and storytelling flips the script"
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
